import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { Observable } from 'rxjs';
import { GlobalFunctions } from '@global/GlobalFunctions';
import { LevelAsset } from './assets-data.service';

/**
 * iCauseTypes
 */
export interface iCauseTypes {
	/** Cause Type */
	CauseType: number
	/** Enumeration Value */
	Enum: string
	/** Description */
	Description: string
}

/**
 * iTriggerTypes
 */
export interface iTriggerTypes {
	/** Trigger Type */
	TriggerType: number
	/** Enumeration Value */
	Enum: string
	/** Description */
	Description: string
}

/**
 * ICaseFile
 */
export interface ICaseFile {
	/** Gets or sets the unique identifier of the case. */
	Id: string

	/** Gets or sets the Asset ID of the case. */
	AssetID: number

	/** Gets or sets the triggerType for the case. */
	TriggerType: number

	/** Gets or sets the trigger for the case. */
	Trigger : string

	/** Gets or sets the trigger ID for the case. */
	TriggerID : number

	/** Gets or sets a detailed description of the case. */
	Description: string

	/** Gets or sets the timestamp when the case was opened. */
	OpenedAt: number

	/** Gets or sets the timestamp when the case was opened. */
	OpenedAtString: string

	/** Gets or sets the if the case is open */
	IsOpen: boolean

	/** Gets or sets the if the case is in CMMS */
	InCMMS: boolean

	/** Gets or sets the timestamp when the case was created in CMMS */
	CMMSCreatedAt: number

	/** Gets or sets the timestamp when the case was created in CMMS */
	CMMSCreatedAtString: string

	/** Gets or sets the User when the case was created in CMMS */
	CMMSCreatedBy: string

	/** Gets or sets the timestamp when the case was closed. */
	ClosedAt: number

	/** Gets or sets the timestamp when the case was closed. */
	ClosedAtString: string

	/** Gets or sets the operator that cosed the case. */
	ClosedBY: string

	/** Gets or sets the cause of the case. */
	Cause: string

	/** Gets or sets the otherComments for the case. */
	OtherComments: string

	/** Gets or sets the case was reasonableToOpen. */
	ReasonableToOpen: boolean

	/** Gets or sets the satisfactionRating for the case. */
	SatisfactionRating: string

	/** Gets or sets the lastModified time the case was modified. */
	LastModified: number

	/** OrganisationUID */
	OrganisationUID: string

	/** Gets or sets the asset associated with this case. */
	Asset: any

	/** Functional Location */
	FL: string

	/** UserID */
	UserID: number

	/** UserID */
	UserIDString: string
}


/**
 * Interface for Database Account Object
 */
export class CaseFile implements ICaseFile {

	constructor(init?: Partial<ICaseFile>) {
		Object.assign(this, init);
	}

	public Id: string = "";
	public AssetID: number = 0;
	public TriggerType: number = 0;
	public Trigger: string = "";
	public TriggerID: number = 0;
	public Description: string = ""
	public OpenedAt: number = 0;
	public OpenedAtString: string = "";
	public IsOpen: boolean;
	public InCMMS: boolean;
	public CMMSCreatedAt: number = 0;
	public CMMSCreatedAtString: string = "";
	public CMMSCreatedBy: string = "";
	public ClosedAt: number = 0;
	public ClosedAtString: string = "";
	public ClosedBY: string = "";
	public Cause: string = "";
	public OtherComments: string = "";
	public ReasonableToOpen: boolean;
	public SatisfactionRating: string = "";
	public LastModified: number = 0;
	public OrganisationUID: string = "";
	public Asset: any;
	public FL: string = "";
	public UserID: number = 0;
	public UserIDString: string = "";
}

/**
 * GraphQLHistory
 */
export class GraphQLHistory {
	constructor(
		public MutationHistoryID: number = 0,
		public TimeStamp: number = 0,
		public MutationID: string = "",
		public MutationSP: string = "",
		public Payload: string = "",
		public UserName: string = "",
		public RowsAffected: number = 0
	) { }
}
/**
 * A work event describes some work carried out at a point in the hierarchy (Asset) 
 */
export interface IWorkEvent {
	/** When the event was created */
	CreationTime: number
	/** The amount of downtime in minutes avoided due to the work */
	DowntimeAvoided: number
	/** The Asset UID for the Work Event */
	AssetUID: string
	/** When the event occurred */
	EventTime: string
	/** The failure which led to the work being carried out */
	FailureMode: string
	/** The point in the hierarchy where the event was created (Asset UID) */
	HierarchyNode: string
	/** The internal identifier used to identify the event */
	Id: string
	/** Whether the work was done as routine maintenance */
	IsRoutine: boolean
	/** A description of the event */
	Message: string
	/** The amount of time in minutes spent on the work */
	TimeSpent: number
	/** The work activity that was carried out */
	WorkActivity: string
	/** Whether to trigger relearning */
	IsRelearning: boolean
	/** Whether the event was prompted by Senseye */
	PromptedByPRISMPdM: boolean
}

/**
 * A work event describes some work carried out at a point in the hierarchy (Asset)
 */
export class WorkEvent implements IWorkEvent {
	constructor(

		/**  When the event was created */
		public CreationTime: number = 0,
		/**  The amount of downtime in minutes avoided due to the work */
		public DowntimeAvoided: number = 0,
		/**  The Asset UID for the Work Event */
		public AssetUID: string = "",
		/**  When the event occurred */
		public EventTime: string = "",
		/**  The failure which led to the work being carried out */
		public FailureMode: string = "",
		/**  The point in the hierarchy where the event was created (Asset UID) */
		public HierarchyNode: string = "",
		/**  The internal identifier used to identify the event */
		public Id: string = "",
		/**  Whether the work was done as routine maintenance */
		public IsRoutine: boolean = false,
		/**  A description of the event */
		public Message: string = "",
		/**  The amount of time in minutes spent on the work */
		public TimeSpent: number = 0,
		/**  The work activity that was carried out */
		public WorkActivity: string = "",
		/**  Whether to trigger relearning */
		public IsRelearning: boolean = false,
		/**  Whether the event was prompted by Senseye */
		public PromptedByPRISMPdM: boolean = false
	) { }

}


@Injectable({
	providedIn: 'root'
})
export class CmmsService {

	/** apiCMMS */
	private apiCMMSRoot = "api/cmms";
	private apiCMMS = "api/{0}/organisation/{1}/cmms";

	/**
	   * Default Constructor
	   * @param http Imports the 'HttpClient' Object for internet requests
	   * @param HTTPFunctionHelper HTTP Function Helper
	   * @param siteSubscriptions site Subscriptions
	   */
	constructor(
		private http: HttpClient,
		private globalFunctions: GlobalFunctions
	) { }


	// /**
	//   * Get a list of Case Files
	//   * @param AccountGUID
	//   * @param OrganisationGUID
	//   * @param CaseID Optional Case ID
	//   * @param queryParams Optional Query Parameters
	//   * @returns Observable<any>
	//   */
	// getHistory$(From: number = 0, To: number = 0, queryParams: {} = {}): Observable<any> {

	// 	let url = this.apiCMMS + '/graphqlhistory'

	// 	url = `${environment.apiUrl}/${url}`;

	// 	// add AccountGUID only if missing
	// 	if (!('From' in queryParams)) {
	// 		(queryParams as any)['From'] = From;
	// 	}

	// 	// add OrganisationGUID only if missing
	// 	if (!('To' in queryParams)) {
	// 		(queryParams as any)['To'] = To;
	// 	}
	// 	return this.globalFunctions.fetchAPI(this.http, url, "GET", queryParams);
	// }

	/**
	  * Get a list of Case Files Cause Types
	  * @returns Observable<any>
	  */
	getCauseTypes$(): Observable<any> {

		let data = {}
		let url = this.apiCMMSRoot + '/causetypes'

		url = `${environment.apiUrl}/${url}`;

		return this.globalFunctions.fetchAPI(this.http, url, "GET", data);
	}

	/**
	  * Get a list of Case Files Trigger Types
	  * @returns Observable<iTriggerTypes[]>
	  */
	getTriggerTypes$(): Observable<iTriggerTypes[]> {

		let data = {}
		let url = this.apiCMMSRoot + '/triggertypes'

		url = `${environment.apiUrl}/${url}`;

		return this.globalFunctions.fetchAPI(this.http, url, "GET", data);
	}

	/**
	  * Get a list of Case Files
	  * @param AccountGUID
	  * @param OrganisationGUID
	  * @param CaseID Optional Case ID
	  * @param queryParams Optional Query Parameters
	  * @returns Observable<any>
	  */
	getCases$(AccountGUID: string, OrganisationGUID: string, queryParams: {} = {}): Observable<ICaseFile[]> {

		let url = this.apiCMMS + '/cases'
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", OrganisationGUID);

		url = `${environment.apiUrl}/${url}`;

		return this.globalFunctions.fetchAPI(this.http, url, "GET", queryParams);
	}

	/**
	  * Post a new Case File
	  * @param AccountGUID
	  * @param OrganisationGUID
	  * @param CaseFile
	  * @returns Observable<any>
	  */
	postCase$(AccountGUID: string, OrganisationGUID: string, CaseFile: CaseFile): Observable<any> {

		let data = JSON.stringify(CaseFile);

		let url = this.apiCMMS + '/cases'
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", OrganisationGUID);

		url = `${environment.apiUrl}/${url}`;

		return this.globalFunctions.fetchAPI(this.http, url, "POST", data);
	}

	/**
	  * Post a new Case File
	  * @param CaseFile
	  * @returns Observable<any>
	  */
	patchCase$(AccountGUID: string, OrganisationGUID: string, CaseFile: CaseFile): Observable<any> {

		let data = JSON.stringify(CaseFile);

		let url = this.apiCMMS + '/cases'
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", OrganisationGUID);

		url = `${environment.apiUrl}/${url}/${CaseFile.Id}`;

		return this.globalFunctions.fetchAPI(this.http, url, "PATCH", data);
	}

	/**
	  * Get a list of Work Events
	  * @param AccountGUID
	  * @param OrganisationGUID
	  * @param AssetID Optional Asset ID
	  * @param CaseID Optional Case ID
	  * @param queryParams Optional Query Parameters
	  * @returns Observable<any>
	  */
	getWorkEvents$(AccountGUID: string, OrganisationGUID: string, AssetID: string = "", CaseID: string = "", queryParams: {} = {}): Observable<any> {

		let url = this.apiCMMS + '/workevents'
		url = url.replace("{0}", AccountGUID);
		url = url.replace("{1}", OrganisationGUID);

		url = `${environment.apiUrl}/${url}`;

		// add CaseID only if missing
		if (!('CaseID' in queryParams)) {
			(queryParams as any)['CaseID'] = CaseID;
		}

		// add CaseID only if missing
		if (!('AssetID' in queryParams)) {
			(queryParams as any)['AssetID'] = AssetID;
		}

		return this.globalFunctions.fetchAPI(this.http, url, "GET", queryParams);
	}
}
